import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, MoveDown, MoveUp } from "lucide-react";
import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
} from "../../@/components/ui/table";
import SkeletonTableLoading from "../../skeletonLoading/SkeletonTableLoading";
import useDebounce from "../../lib/utils";
import { Input } from "../../@/components/ui/input";
import Pagination from "../../components/Pagination";
import { useGetAllCategoriesMutation } from "../../api/api";
import { AddEditCategoryDialog } from "./AddEditCategoryDialog";
import { FiSearch } from "react-icons/fi";
import ServiceTypeDropdown from "./ServiceTypeDropDown";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends unknown, TValue> {
    isTooltip?: boolean;
  }
}

interface Category {
  name: string;
  id: number;
  serviceTypeId: number;
  serviceTypeName: string;
}

const CategoryConfiguration: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const [data, setData] = useState<Category[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, _] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "asc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>(
    "name"
  );
  const [selectedServiceType, setSelectedServiceType] = useState<string>("");

  const [getCategoryData, { error, isLoading }] = useGetAllCategoriesMutation();

  const fetchData = useCallback(async () => {
    try {
      const serviceTypeIdValue = selectedServiceType
        ? parseInt(selectedServiceType)
        : undefined;

      const requestPayload = {
        getAllCategoriesRequestDto: {
          searchQuery:
            debouncedSearchQuery !== "" ? debouncedSearchQuery : undefined,
          pageNumber,
          pageSize,
          getTotalCount: true,
          sortingOrder,
          sortingColumn,
          serviceTypeId:
            serviceTypeIdValue === 0 ? undefined : serviceTypeIdValue,
        },
      };

      const response: any = await getCategoryData(requestPayload).unwrap();
      setData(response?.data?.categories || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error("Failed to get products: ", err);
    }
  }, [
    getCategoryData,
    debouncedSearchQuery,
    pageNumber,
    sortingColumn,
    sortingOrder,
    selectedServiceType,
    pageSize,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, pageNumber]);

  const handleSorting = (column: string) => {
    if (sortingColumn === column) {
      setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
    } else {
      setSortingColumn(column);
      setSortingOrder("asc");
    }
    setPageNumber(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
    setSearchTerm(event.target.value);
  };

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  const columns = useMemo<ColumnDef<Category>[]>(
    () => [
      {
        accessorKey: "name",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("name")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Category Name{" "}
            {sortingColumn === "name" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 125,
        cell: ({ cell }) => (
          <span className="">{cell.getValue() as string}</span>
        ),
      },
      {
        accessorKey: "serviceTypeName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("serviceTypeName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Service Type{" "}
            {sortingColumn === "serviceTypeName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 125,
        cell: ({ cell }) => <span>{cell.getValue() as string}</span>,
      },
      {
        id: "actions",
        header: () => (
          <div className="flex items-center justify-center">Action</div>
        ),
        size: 50,
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <AddEditCategoryDialog
              isEditOnly={true}
              categoryId={row.original.id}
              setPageNumber={setPageNumber}
              setSearchTerm={setSearchTerm}
              fetchData={fetchData}
              selectedServiceType={row.original.serviceTypeId.toString()}
            />
          </div>
        ),
        meta: {
          isTooltip: false,
        },
      },
    ],
    [
      sortingColumn,
      sortingOrder,
      setPageNumber,
      setSearchTerm,
      fetchData,
      selectedServiceType,
    ]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
    },
  });

  return (
    <div className="overflow-hidden pr-2 h-full ">
      <div className="flex justify-between items-center flex-wrap mb-2 gap-2">
        <div className="flex justify-between items-start gap-x-10 w-full flex-col sm:flex-row gap-2">
          <div className="w-full sm:w-auto">
            <AddEditCategoryDialog
              isEditOnly={false}
              fetchData={fetchData}
              setPageNumber={setPageNumber}
              setSearchTerm={setSearchTerm}
              selectedServiceType={selectedServiceType}
            />
          </div>

          <div className="flex gap-2 flex-col sm:flex-row w-full sm:w-auto">
            {/* <div className="flex justify-between bg-white rounded-lg border p-[1px] border-gray-300 w-full md:w-auto">
              {options.map((option) => (
                <button
                  key={option.name}
                  onClick={() => {
                    setPageNumber(1);
                    setSelected(option);
                  }}
                  className={`flex-1 w-[4.5rem] p-2 text-center text-[10px] ${
                    selected.name === option.name
                      ? "bg-customGreenLight text-customGreenColor font-base"
                      : "text-gray-500 hover:bg-gray-100"
                  } rounded-lg ${
                    option.name === "All Category" ? "rounded-l-lg" : ""
                  }`}
                >
                  {option.name}
                </button>
              ))}
            </div> */}

            <ServiceTypeDropdown
              selectedServiceType={selectedServiceType}
              onChange={(value) => {
                setSelectedServiceType(value);
                setPageNumber(1);
              }}
            />

            <div className="relative flex justify-center text-customGrayText items-center w-full sm:w-48">
              <FiSearch className="absolute left-2 text-lg" />
              <Input
                placeholder="Search category"
                className="text-sm text-left h-9 pl-8"
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="overflow-y-auto h-[calc(100vh-14rem)]">
          <div className="w-full rounded-lg shadow bg-white h-full">
            <Table>
              <TableHeader className="bg-customGreenColor text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        className={`font-medium text-sm text-nowrap`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
            <SkeletonTableLoading />
          </div>
        </div>
      ) : error ? (
        <div>Error loading categories.</div>
      ) : (
        <>
          <div className="overflow-y-auto h-[calc(100vh-14rem)]">
            <div className="overflow-x-auto w-full rounded-lg shadow bg-white h-full">
              <Table>
                <TableHeader className="sticky top-0 bg-customGreenColor text-white">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={`font-medium text-sm text-nowrap`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-center"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={`font-medium text-sm whitespace-nowrap overflow-hidden`}
                            isTooltip={
                              cell.column.columnDef.meta?.isTooltip ?? true
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
      {data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 pb-4 overflow-hidden">
          <div className="text-[#667085]">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(totalCount / pageSize)}
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default CategoryConfiguration;
